import type { ReactElement } from 'react';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { CatalanFlag, FranceFlag, ItalyFlag, SpainFlag, UkFlag } from './icons';
import { newLocales, type NewLocale } from './locales';

/**
 * The switcher's rows: the published locales from `locales.ts`, in that order, with their labels.
 *
 * Built by mapping that list rather than repeating it, so adding a locale there cannot leave the
 * switcher behind — and the `Record` below makes TypeScript demand a label for the new one.
 */
const LABELS: Record<NewLocale, { short: string; label: string }> = {
	es: { short: 'ES', label: 'Español' },
	en: { short: 'EN', label: 'English' },
	ca: { short: 'CA', label: 'Català' },
	fr: { short: 'FR', label: 'Français' },
	it: { short: 'IT', label: 'Italiano' },
};

type Language = { code: NewLocale; short: string; label: string };

// `.map` over a tuple widens to a plain array, losing the "there is always a first one" that the
// header's fallback leans on. `newLocales` is a non-empty literal tuple, so this is true by
// construction — and it stays true whatever is added to that list.
export const LANGUAGES = newLocales.map((code) => ({ code, ...LABELS[code] })) as [Language, ...Language[]];

const FLAGS: Partial<Record<PortalLocale, (props: { className?: string }) => ReactElement>> = {
	es: SpainFlag,
	en: UkFlag,
	ca: CatalanFlag,
	it: ItalyFlag,
	fr: FranceFlag,
};

export function LanguageFlag({ code, className }: { code: PortalLocale; className?: string }) {
	const Flag = FLAGS[code] ?? SpainFlag;
	return <Flag className={className} />;
}
